import { addDays, addMonths } from "date-fns";
import { useCallback, useMemo } from "react";

import { useUIStore } from "@/stores/uiStore";

const validateDate = (date: string | Date) => {
  if (!date) {
    return false;
  }
  const newDate = new Date(date);

  return !Number.isNaN(newDate.getTime());
};

/**
 *
 * @param date
 * @param freeze It is recommended to set it to `true`.
 * @returns
 */
export function convertToAPIDate(
  date: string | Date,
  freeze?: boolean,
): string | undefined {
  if (validateDate(date)) {
    const dateInstance = new Date(date);
    const year = dateInstance.getFullYear();
    const month = convertToTwoDigits(dateInstance.getMonth() + 1);
    const day = convertToTwoDigits(dateInstance.getDate());

    const utcYear = dateInstance.getUTCFullYear();
    const utcMonth = convertToTwoDigits(dateInstance.getUTCMonth() + 1);
    const utcDay = convertToTwoDigits(dateInstance.getUTCDate());

    return freeze
      ? `${year}-${month}-${day}`
      : `${utcYear}-${utcMonth}-${utcDay}`;
  }
  return undefined;
}

/**
 *
 * @param date
 * @param freeze It is recommended to set it to `false`.
 * @returns
 */
export function convertToFullAPIDate(
  date: string | Date,
  freeze?: boolean,
): string {
  if (validateDate(date)) {
    const dateInstance = new Date(date);
    const year = dateInstance.getFullYear();
    const month = convertToTwoDigits(dateInstance.getMonth() + 1);
    const day = convertToTwoDigits(dateInstance.getDate());
    const hour = convertToTwoDigits(dateInstance.getHours());
    const minute = convertToTwoDigits(dateInstance.getMinutes());

    const utcYear = dateInstance.getUTCFullYear();
    const utcMonth = convertToTwoDigits(dateInstance.getUTCMonth() + 1);
    const utcDay = convertToTwoDigits(dateInstance.getUTCDate());
    const utcHour = convertToTwoDigits(dateInstance.getUTCHours());
    const utcMinute = convertToTwoDigits(dateInstance.getUTCMinutes());

    return freeze
      ? `${year}-${month}-${day} ${hour}:${minute}`
      : `${utcYear}-${utcMonth}-${utcDay} ${utcHour}:${utcMinute}`;
  }
  return "";
}

export function convertToTime(date: string | Date, freeze?: boolean): string {
  if (validateDate(date)) {
    const dateInstance = new Date(date);
    const hour = convertToTwoDigits(dateInstance.getHours());
    const minute = convertToTwoDigits(dateInstance.getMinutes());

    const utcHour = convertToTwoDigits(dateInstance.getUTCHours());
    const utcMinute = convertToTwoDigits(dateInstance.getUTCMinutes());

    return freeze ? `${hour}:${minute}` : `${utcHour}:${utcMinute}`;
  }
  return "";
}

export function convertToTwoDigits(num: number | undefined): string {
  if (!num) {
    return "00";
  }
  if (num.toString()?.length >= 3) {
    return num?.toString();
  }
  return `00${num}`.slice(-2);
}

export function useConvertDate() {
  const locale = useUIStore((state) => state.locale);

  const numericDateTime = useCallback(
    (date: string | Date): string => {
      if (!validateDate(date)) return "- - -";
      return new Date(date).toLocaleDateString(locale, {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    [locale],
  );

  const humanizeDate = useCallback(
    (date: string | Date): string => {
      if (validateDate(date)) {
        return new Date(date).toLocaleString(locale, {
          year: "numeric",
          month: "long",
          day: "2-digit",
        });
      }
      return "- - -";
    },
    [locale],
  );

  const humanizeTime = useCallback(
    (date: string | Date): string => {
      if (validateDate(date)) {
        return new Date(date).toLocaleString(locale, {
          hour: "2-digit",
          minute: "2-digit",
        });
      }
      return "- - -";
    },
    [locale],
  );

  const humanizeFullTime = useCallback(
    (date: string | Date): string => {
      if (validateDate(date)) {
        return new Date(date).toLocaleString(locale, {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        });
      }
      return "- - -";
    },
    [locale],
  );

  const humanizeFullDate = useCallback(
    (date: string | Date | undefined): string => {
      if (!date) return "- - -";
      if (validateDate(date)) {
        return new Date(date).toLocaleString(locale, {
          year: "numeric",
          month: "long",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        });
      }
      return "- - -";
    },
    [locale],
  );

  return useMemo(
    () => ({
      humanizeDate,
      humanizeTime,
      humanizeFullTime,
      humanizeFullDate,
      numericDateTime,
    }),
    [
      humanizeDate,
      humanizeFullDate,
      humanizeFullTime,
      humanizeTime,
      numericDateTime,
    ],
  );
}

export const getTomorrow = (days = 1) => addDays(new Date(), days);
export const getNextMonth = (nextMonth = 1) => addMonths(new Date(), nextMonth);

export function combineDates(date1: Date, date2: Date): Date {
  // Extract year, month, and day from date1
  const year = date1.getFullYear();
  const month = date1.getMonth();
  const day = date1.getDate();

  // Extract hours, minutes, seconds, and milliseconds from date2
  const hours = date2.getHours();
  const minutes = date2.getMinutes();

  // Create a new Date by combining the extracted parts
  return new Date(year, month, day, hours, minutes);
}

export function convertToDate(value: Date | string) {
  if (!!value && validateDate(value)) {
    return new Date(value);
  }
}
